import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import { pounds as money } from "../../lib/money";
import { agoPhrase } from "../../lib/time";
import type {
  AmContactNext,
  AmCustomer,
  AmDashboard as Dashboard,
  AmOwnerOption,
  SpendBasis,
} from "../../lib/crm-types";
import type { User } from "../../types";
import { CardChart } from "../../components/reports/card/ReportCard";
import { RiskBadges } from "../../components/crm/am/RiskBadge";
import { Tabs } from "../../components/ui/Tabs";
import { cn } from "../../components/ui/cn";

/**
 * The account manager's dashboard.
 *
 * Opened to answer four questions and nothing else: who is lapsing, who to ring
 * next, what is open, and how the book is doing. Everything on it earns its
 * place against one of those, which is why there is no activity feed and no
 * league table -- the sales desk next door already has both, and this screen is
 * about accounts rather than about people.
 *
 * ## Two halves, and the page says which is which
 *
 * The CRM half is complete. The commercial half is as complete as what the desk
 * has written down: spend is summed from the orders recorded against each
 * account, so an account with none reports null rather than zero, and the page
 * says "no orders recorded" rather than "£0". `spendBasis` says which world we
 * are in, which today means saying when the numbers are the development
 * fixture -- a manager must never mistake invented revenue for takings, so that
 * gets said on the screen in as many words.
 *
 * ## Scope lives in the URL
 *
 * So a manager can send somebody a link to their own book. `useSearchParams`
 * rather than local state for the same reason the ticket list does it.
 */
export function AmDashboard({ user }: { user: User }) {
  const [params, setParams] = useSearchParams();
  const scope = params.get("scope") ?? "mine";
  const filter = params.get("filter") ?? "";
  const sort = params.get("sort") ?? "risk";
  /* An account manager's id, or "none". Narrows the table only -- the tiles
     above keep measuring the tab, which is the comparison it exists for. */
  const owner = params.get("owner") ?? "";

  const scopes = useQuery({
    queryKey: queryKeys.crmAmScopes,
    queryFn: crmApi.amScopes,
    staleTime: 5 * 60_000,
  });

  const dashboard = useQuery({
    queryKey: queryKeys.crmAmDashboard(scope),
    queryFn: () => crmApi.amDashboard(scope),
    staleTime: 60_000,
  });

  const customers = useQuery({
    queryKey: queryKeys.crmAmCustomers(scope, filter, sort, owner),
    queryFn: () =>
      crmApi.amCustomers(
        scope,
        (filter || undefined) as "at-risk" | "open-quotes" | "quiet" | undefined,
        sort as "risk" | "name" | "contact" | "spend",
        owner || undefined,
      ),
    staleTime: 60_000,
  });

  /**
   * Change one or more query parameters in a single write.
   *
   * Takes a whole patch rather than a key and a value, and that is not
   * tidiness. `setSearchParams` is asynchronous, and its functional form is
   * handed the *committed* params -- so two calls in the same handler both
   * start from the same base and the second silently discards the first. The
   * quiet tile sets a filter and a sort together, and written as two calls it
   * applied only the sort: the table re-ordered but never filtered.
   *
   * An empty string deletes, so callers can clear a parameter without a second
   * code path.
   */
  const setQuery = (patch: Record<string, string>) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(patch)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  };

  const tabs = useMemo(
    () =>
      (scopes.data?.scopes ?? []).map((entry) => ({
        value: entry.key,
        label: entry.label,
        badge: entry.accounts,
      })),
    [scopes.data],
  );

  const data = dashboard.data;

  return (
    <div className="mx-auto flex h-full w-full max-w-6xl flex-col overflow-y-auto p-4 sm:p-6">
      <header className="mb-3 flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold text-content">Companies dashboard</h1>
        {data ? <SpendNotice basis={data.spendBasis} /> : null}
      </header>

      {tabs.length > 0 ? (
        /* Scrolls sideways rather than wrapping. Four tabs do not fit a phone,
           and the one that wrapped sat alone on a second line under a rule
           that no longer reached it -- which reads as a heading, not a tab.
           `w-max` sizes the bar to its tabs so it never wraps at all, and
           `min-w-full` keeps the rule across the page when they are short. */
        <div className="-mx-4 mb-4 shrink-0 overflow-x-auto px-4 sm:mx-0 sm:px-0">
          <Tabs
            tabs={tabs}
            value={scope}
            /* The owner goes with the tab. Carrying "Priya" onto a tab she
               owns nothing in leaves an empty table and no visible reason for
               it -- the filter is off screen, in a header below the fold. */
            onChange={(next) => setQuery({ scope: next, owner: "" })}
            label="Account manager views"
            className="w-max min-w-full"
          />
        </div>
      ) : null}

      {dashboard.isLoading ? (
        <p className="p-8 text-center text-xs text-faint">Loading…</p>
      ) : !data ? (
        <p className="p-8 text-center text-xs text-faint">
          The dashboard could not be loaded.
        </p>
      ) : data.accounts === 0 ? (
        <Empty scope={scope} />
      ) : (
        <>
          <KpiRow
            data={data}
            activeFilter={filter}
            onFilter={(next) =>
              /* One write, both parameters. The quiet tile asks "who has gone
                 silent longest", so it sets the sort as well -- landing on a
                 risk-ordered table would answer a different question than the
                 one that was clicked. */
              setQuery({
                filter: next,
                sort: next === "quiet" ? "contact" : "risk",
              })
            }
          />

          <SpendChart chart={data.chart} />

          <ContactNext entries={data.contactNext} />

          <CustomerTable
            rows={customers.data?.customers ?? []}
            loading={customers.isLoading}
            filter={filter}
            sort={sort}
            owner={owner}
            owners={customers.data?.owners ?? []}
            onFilter={(next) => setQuery({ filter: next })}
            onSort={(next) => setQuery({ sort: next })}
            onOwner={(next) => setQuery({ owner: next })}
            currentUserId={user.id}
            opportunities={scope === "opportunity"}
          />
        </>
      )}
    </div>
  );
}

/**
 * Where the money figures came from.
 *
 * Only shown when there is something to warn about. A banner saying "these
 * numbers are real" on every page load is noise that trains people to skip the
 * one that matters.
 */
function SpendNotice({ basis }: { basis: SpendBasis }) {
  if (basis === "recorded") return null;

  return (
    <span
      title="SPEND_FIXTURE is on. Every spend figure on this page is invented for development and means nothing."
      className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
    >
      Spend figures are invented
    </span>
  );
}

// ── The three tiles ──────────────────────────────────────────────────────────

function KpiRow({
  data,
  activeFilter,
  onFilter,
}: {
  data: Dashboard;
  activeFilter: string;
  onFilter: (filter: string) => void;
}) {
  const { kpis } = data;
  const change =
    kpis.spendMonthToDate !== null && kpis.spendPreviousMonthToDate !== null
      ? kpis.spendMonthToDate - kpis.spendPreviousMonthToDate
      : null;

  return (
    <div className="mb-5 grid gap-2 sm:grid-cols-3 sm:gap-3">
      <Tile
        label="Spend this month"
        /* Null renders as "no orders recorded", never as £0 -- one means
           nobody has written an order down and the other means a terrible
           month, and a manager should never have to guess which they are
           looking at. This said "Team not connected" when the figures were
           fetched rather than summed; there is nothing to connect now, so the
           only way to get here is an empty book. */
        value={kpis.spendMonthToDate === null ? null : money(kpis.spendMonthToDate)}
        note={
          change === null
            ? "No orders recorded this month"
            : `${change >= 0 ? "+" : "−"}${money(Math.abs(change))} against last month to date`
        }
        tone={change === null ? "muted" : change >= 0 ? "good" : "bad"}
      />

      <Tile
        label="Gone quiet"
        value={String(kpis.quiet)}
        note="Past their contact threshold"
        tone={kpis.quiet > 0 ? "bad" : "muted"}
        active={activeFilter === "quiet"}
        onClick={() => onFilter(activeFilter === "quiet" ? "" : "quiet")}
      />

      <Tile
        label="Pending tasks"
        value={String(kpis.pendingTasks)}
        note="Open on your accounts"
        tone="muted"
        active={activeFilter === "at-risk"}
        onClick={() => onFilter(activeFilter === "at-risk" ? "" : "at-risk")}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  note,
  tone,
  active,
  onClick,
}: {
  label: string;
  /** Null means "no figure available", which is drawn differently from zero. */
  value: string | null;
  note: string;
  tone: "good" | "bad" | "muted";
  active?: boolean;
  onClick?: () => void;
}) {
  /*
   * Stacked on a desktop, where three tiles share a row. On a phone they are
   * one under another, and stacked there each was ninety pixels for a label, a
   * number and a line -- the three of them a screen before the chart. So
   * below `sm` the figure moves to the right of the words and a tile is one
   * line tall plus its note.
   */
  const body = (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-3 sm:block">
      <div className="text-[11px] font-medium uppercase tracking-wide text-faint">
        {label}
      </div>
      <div
        className={cn(
          "col-start-2 row-span-2 row-start-1 text-xl font-semibold tabular-nums sm:mt-1 sm:text-2xl",
          value === null ? "text-faint" : "text-content",
        )}
      >
        {value ?? "—"}
      </div>
      <div
        className={cn(
          "mt-0.5 text-[11px]",
          tone === "good"
            ? "text-emerald-600 dark:text-emerald-400"
            : tone === "bad"
              ? "text-red-600 dark:text-red-400"
              : "text-faint",
        )}
      >
        {note}
      </div>
    </div>
  );

  const shell = cn(
    "rounded-lg border bg-surface px-3 py-2.5 text-left",
    active ? "border-brand-500 ring-1 ring-brand-500/30" : "border-line",
  );

  if (!onClick) return <div className={shell}>{body}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(shell, "transition-colors hover:border-brand-400")}
    >
      {body}
    </button>
  );
}

// ── The chart ────────────────────────────────────────────────────────────────

/**
 * Twelve months of combined spend.
 *
 * Through the same `ChartFrame` the helpdesk reports use, so it inherits the
 * theme, the tooltip and the code-split recharts bundle rather than pulling a
 * second charting path into the CRM.
 */
function SpendChart({
  chart,
}: {
  chart: Dashboard["chart"];
}) {
  const rows = useMemo(
    () =>
      (chart ?? []).map((point) => ({
        label: monthLabel(point.month),
        values: { spend: point.net },
      })),
    [chart],
  );

  return (
    <section className="mb-5 rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-medium text-content">Spend, last 12 months</h2>
        <span className="text-[11px] text-faint">
          Combined across the accounts in this view
        </span>
      </header>

      <div className="p-3">
        {chart === null ? (
          <p className="py-10 text-center text-xs text-faint">
            {/* One message now. There is no "not connected" state left: the
                line is summed from recorded orders, so an empty chart means
                nobody has recorded any. */}
            No orders recorded in this period.
          </p>
        ) : (
          <CardChart
            chart="line"
            rows={rows}
            series={[{ key: "spend", label: "Spend" }]}
            colorFor={() => "#e0447d"}
            format={(value) => (value === null ? "—" : money(value))}
            height={200}
            emptyMessage="Nothing ordered in the last twelve months."
          />
        )}
      </div>
    </section>
  );
}

// ── The hero list ────────────────────────────────────────────────────────────

/**
 * Who to ring next.
 *
 * The reason is on every row and is not optional -- it comes from the same
 * function that decided the account was at risk, so a row cannot appear here
 * without saying why it did.
 */
function ContactNext({
  entries,
}: {
  entries: AmContactNext[];
}) {
  return (
    <section className="mb-5 rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-baseline gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-medium text-content">Contact next</h2>
        <span className="text-[11px] text-faint">
          Ranked by value against risk
        </span>
      </header>

      {entries.length === 0 ? (
        <p className="p-6 text-center text-xs text-faint">
          Nothing needs chasing. Every account in this view is inside its rules.
        </p>
      ) : (
        <ol className="divide-y divide-line">
          {entries.map((entry, index) => (
            <li key={entry.companyId}>
              <Link
                to={`/crm/companies/${entry.companyId}`}
                className="flex items-center gap-3 px-3 py-2 hover:bg-surface-2"
              >
                <span className="w-5 shrink-0 text-right text-[11px] tabular-nums text-faint">
                  {index + 1}
                </span>

                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-content">
                    {entry.name}
                  </span>
                  <span className="block truncate text-[11px] text-faint">
                    {entry.ownerName ?? "Unmanaged"}
                    {entry.spendYearToDate !== null
                      ? ` · ${money(entry.spendYearToDate)} this year`
                      : ""}
                  </span>
                </span>

                <RiskBadges reasons={entry.reasons} />
              </Link>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

// ── The table ────────────────────────────────────────────────────────────────

const FILTERS = [
  { value: "", label: "All" },
  { value: "at-risk", label: "At risk" },
  { value: "quiet", label: "Gone quiet" },
  { value: "open-quotes", label: "Open quotes" },
] as const;

const SORTS = [
  { value: "risk", label: "Risk" },
  { value: "contact", label: "Longest since contact" },
  { value: "spend", label: "Spend" },
  { value: "name", label: "Name" },
] as const;

function CustomerTable({
  rows,
  loading,
  filter,
  sort,
  owner,
  owners,
  onFilter,
  onSort,
  onOwner,
  currentUserId,
  opportunities,
}: {
  rows: AmCustomer[];
  loading: boolean;
  filter: string;
  sort: string;
  owner: string;
  /** Everybody in this tab, counted before the owner filter narrowed it. */
  owners: AmOwnerOption[];
  onFilter: (value: string) => void;
  onSort: (value: string) => void;
  onOwner: (value: string) => void;
  currentUserId: number;
  /** The "Needs account managing" scope, which asks a different question. */
  opportunities: boolean;
}) {
  /*
   * Only once there is a choice to make. "Mine" is one person's book by
   * definition and the opportunity tab is nobody's, so on both of those the
   * select would be a control with a single option -- which reads as an
   * unfinished filter rather than as an answer.
   *
   * The test is on the options rather than on the tab, so a desk that happens
   * to have one active manager also gets the honest answer, and the page never
   * has to know which scope keys mean what.
   */
  const pickable = owners.length > 1;

  return (
    <section className="mb-6 rounded-lg border border-line bg-surface">
      <header className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
        <h2 className="text-sm font-medium text-content">Companies</h2>
        <span className="text-[11px] text-faint">{rows.length}</span>

        {/* Its own full-width line on a phone, the selects sharing it. Beside
            the heading they were pushed off the card's right edge. */}
        <span className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
          {pickable ? (
            <select
              value={owner}
              onChange={(event) => onOwner(event.target.value)}
              aria-label="Account manager"
              className="h-8 min-w-0 flex-1 rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400 sm:h-7 sm:max-w-44 sm:flex-none"
            >
              <option value="">All account managers</option>
              {owners.map((entry) => (
                <option
                  key={entry.id ?? "none"}
                  value={entry.id === null ? "none" : String(entry.id)}
                >
                  {entry.name} ({entry.accounts})
                </option>
              ))}
            </select>
          ) : null}

          <select
            value={filter}
            onChange={(event) => onFilter(event.target.value)}
            aria-label="Filter"
            className="h-8 min-w-0 flex-1 rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400 sm:h-7 sm:flex-none"
          >
            {FILTERS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>

          <select
            value={sort}
            onChange={(event) => onSort(event.target.value)}
            aria-label="Sort"
            className="h-8 min-w-0 flex-1 rounded-md border border-line bg-surface px-2 text-xs text-content outline-none focus:border-brand-400 sm:h-7 sm:flex-none"
          >
            {SORTS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </select>
        </span>
      </header>

      {loading ? (
        <p className="p-6 text-center text-xs text-faint">Loading…</p>
      ) : rows.length === 0 ? (
        /* An empty opportunity tab is a normal, good state rather than a
           filter that matched nothing, and saying so is also the only place
           anybody finds out how to put something in it. */
        <p className="p-6 text-center text-xs text-faint">
          {opportunities
            ? "Nothing flagged. Open an account nobody manages and flag it as an opportunity."
            : "No customers match that filter."}
        </p>
      ) : (
        /* Scrolls inside itself. Eleven columns will not fit a laptop, and a
           page that scrolls sideways as a whole loses the tab bar with it. */
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-faint">
              <tr>
                <Th sticky>Company</Th>
                <Th align="right">Spend YTD</Th>
                <Th align="right">vs last year</Th>
                <Th align="right">MTD</Th>
                <Th align="right">vs last month</Th>
                <Th align="right">Last order</Th>
                <Th align="right">Last contact</Th>
                <Th align="right">Open</Th>
                {/*
                  The last column changes with the tab, because the question
                  does. Every other scope is a book somebody owns, where "what
                  is wrong with this account" is the thing to scan for. The
                  opportunity tab is a list of accounts nobody owns, where
                  nothing is wrong yet and the useful column is why somebody
                  flagged it -- the risk badges there would be a column of
                  "never contacted" on every row, which is true of an unmanaged
                  account by definition and tells nobody anything.
                */}
                <Th>{opportunities ? "Why it was flagged" : "Needs attention"}</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <Row
                  key={row.companyId}
                  row={row}
                  mine={row.ownerUserId === currentUserId}
                  opportunities={opportunities}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Row({
  row,
  mine,
  opportunities,
}: {
  row: AmCustomer;
  mine: boolean;
  /** The "Needs account managing" tab, where the last column says why. */
  opportunities: boolean;
}) {
  return (
    <tr
      className={cn(
        "border-t border-line hover:bg-surface-2",
        mine && "bg-brand-50/40 dark:bg-brand-950/20",
      )}
    >
      {/* Pinned on a phone, where the figures scroll under it: a row of
          pounds with its company scrolled off the left is a row of nobody's
          pounds. Painted opaque, because the cells pass beneath it, with the
          row's own "mine" tint laid over as an image so it still matches. */}
      <td
        className={cn(
          "px-3 py-1.5",
          STICKY,
          mine &&
            "max-sm:bg-linear-to-r max-sm:from-brand-50/40 max-sm:to-brand-50/40 dark:max-sm:from-brand-950/20 dark:max-sm:to-brand-950/20",
        )}
      >
        <Link
          to={`/crm/companies/${row.companyId}`}
          className="flex items-center gap-1.5 text-sm text-content hover:text-brand-600 dark:hover:text-brand-400"
        >
          <span className="min-w-0 truncate">{row.name}</span>
          {row.isKeyAccount ? (
            <span
              title="A manager marked this a key account"
              className="shrink-0 rounded-full bg-brand-600 px-1.5 py-0.5 text-[10px] font-medium text-white"
            >
              Key
            </span>
          ) : null}
        </Link>
        <div className="truncate text-[11px] text-faint">
          {row.ownerName ?? "Unmanaged"}
        </div>
      </td>

      <Td align="right">{row.spendYearToDate === null ? null : money(row.spendYearToDate)}</Td>
      <Td align="right">
        <Growth now={row.spendYearToDate} before={row.spendPreviousYearToDate} />
      </Td>
      <Td align="right">{row.spendMonthToDate === null ? null : money(row.spendMonthToDate)}</Td>
      <Td align="right">
        <Growth now={row.spendMonthToDate} before={row.spendPreviousMonthToDate} />
      </Td>

      <Td align="right">
        <Since days={row.daysSinceOrder} limit={row.thresholds.noOrderDays} />
      </Td>
      <Td
        align="right"
        title={
          row.lastActivityAt
            ? `Anything at all happened ${new Date(row.lastActivityAt).toLocaleDateString("en-GB")} — including record changes, which do not count as contact`
            : undefined
        }
      >
        <Since days={row.daysSinceContact} limit={row.thresholds.noContactDays} />
      </Td>

      <Td align="right">
        <OpenItems row={row} />
      </Td>

      <td className="px-3 py-1.5">
        {opportunities ? (
          <div className="min-w-0">
            {row.opportunityNote ? (
              <p className="text-xs text-content">{row.opportunityNote}</p>
            ) : (
              /* A real state: the note is optional. "No reason given" is more
                 use than a blank cell, which reads as data that failed to
                 load. */
              <p className="text-xs text-faint">No reason given</p>
            )}
            <p className="text-[11px] text-faint">
              {row.opportunityStarredByName ?? "Somebody"}
              {row.opportunityStarredAt
                ? `, ${agoPhrase(row.opportunityStarredAt)}`
                : ""}
            </p>
          </div>
        ) : (
          <RiskBadges reasons={row.reasons} />
        )}
      </td>
    </tr>
  );
}

/**
 * Days since something, flagged once it is past the account's own threshold.
 *
 * The threshold comes down with the row rather than being assumed to be 30,
 * because a company or its team may have changed it -- and a red number against
 * an account whose manager deliberately set a 90-day rule would be the page
 * arguing with its own settings.
 */
function Since({ days, limit }: { days: number | null; limit: number }) {
  if (days === null) {
    return (
      <span title="Never" className="text-faint">
        Never
      </span>
    );
  }

  const over = days > limit;

  return (
    <span
      title={`${days} days, against a ${limit}-day rule`}
      className={cn("tabular-nums", over ? "text-red-600 dark:text-red-400" : "text-muted")}
    >
      {days}d
    </span>
  );
}

/** Growth as a percentage, with a direction that is not only a colour. */
function Growth({ now, before }: { now: number | null; before: number | null }) {
  if (now === null || before === null) {
    return <span className="text-faint">—</span>;
  }

  /* No percentage from a zero base. A customer who spent nothing last year and
     something this year has not grown by infinity -- they are new. */
  if (before <= 0) {
    return (
      <span title="Nothing to compare against" className="text-faint">
        New
      </span>
    );
  }

  const change = ((now - before) / before) * 100;
  const up = change >= 0;

  return (
    <span
      title={`${money(before)} at the same point before`}
      className={cn(
        "tabular-nums",
        Math.abs(change) < 1
          ? "text-muted"
          : up
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-red-600 dark:text-red-400",
      )}
    >
      {up ? "▲" : "▼"}
      {Math.abs(Math.round(change))}%
    </span>
  );
}

/**
 * Open quotes, tickets and calls, each a link into the thing itself.
 *
 * One cell rather than three columns: they are all "how much is in flight
 * here", they are usually zero, and three mostly-empty columns is a lot of
 * table to say nothing.
 */
function OpenItems({ row }: { row: AmCustomer }) {
  const items: { label: string; count: number; to: string; title: string }[] = [];

  if (row.openQuotes) {
    items.push({
      label: "Q",
      count: row.openQuotes,
      to: `/crm/companies/${row.companyId}`,
      title: `${row.openQuotes} open quote${row.openQuotes === 1 ? "" : "s"}`,
    });
  }
  if (row.openTickets) {
    items.push({
      label: "T",
      count: row.openTickets,
      to: `/crm/companies/${row.companyId}`,
      title: `${row.openTickets} open ticket${row.openTickets === 1 ? "" : "s"}`,
    });
  }
  if (row.openCalls) {
    items.push({
      label: "C",
      count: row.openCalls,
      to: `/sales/calls?companyId=${row.companyId}`,
      title: `${row.openCalls} open sales call${row.openCalls === 1 ? "" : "s"}`,
    });
  }
  if (row.pendingTasks) {
    items.push({
      label: "★",
      count: row.pendingTasks,
      to: `/crm/companies/${row.companyId}`,
      title: `${row.pendingTasks} pending task${row.pendingTasks === 1 ? "" : "s"}`,
    });
  }

  if (items.length === 0) return <span className="text-faint">—</span>;

  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      {items.map((item) => (
        <Link
          key={item.label}
          to={item.to}
          title={item.title}
          className="rounded bg-surface-2 px-1.5 py-0.5 text-[11px] tabular-nums text-muted-on-tint hover:text-brand-600 dark:hover:text-brand-400"
        >
          {item.label}
          {item.count}
        </Link>
      ))}
    </span>
  );
}

// ── Furniture ────────────────────────────────────────────────────────────────

/**
 * The company column's pinning, below `sm` only. Narrowed there too, so a long
 * name truncates instead of taking the whole screen it is meant to share.
 */
const STICKY =
  "max-sm:sticky max-sm:left-0 max-sm:z-[1] max-sm:max-w-36 max-sm:bg-surface max-sm:shadow-[4px_0_6px_-4px_rgb(0_0_0/0.15)]";

function Th({
  children,
  align,
  sticky,
}: {
  children: React.ReactNode;
  align?: "right";
  sticky?: boolean;
}) {
  return (
    <th
      className={cn(
        "whitespace-nowrap px-3 py-1.5 font-medium",
        align === "right" && "text-right",
        sticky && STICKY,
      )}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  title,
}: {
  children: React.ReactNode;
  align?: "right";
  title?: string;
}) {
  return (
    <td
      title={title}
      className={cn(
        "whitespace-nowrap px-3 py-1.5 text-xs text-muted",
        align === "right" && "text-right",
      )}
    >
      {children ?? <span className="text-faint">—</span>}
    </td>
  );
}

function Empty({ scope }: { scope: string }) {
  return (
    <p className="p-10 text-center text-xs text-faint">
      {scope === "key"
        ? "No accounts are marked as key customers yet. Mark one on its company page."
        : scope === "mine"
          ? "You do not manage any accounts yet."
          : "There are no accounts in this view."}
    </p>
  );
}

/** "2026-08" -> "Aug 26", which is what fits under a twelve-point axis. */
function monthLabel(month: string): string {
  const [year, index] = month.split("-");
  const date = new Date(Number(year), Number(index) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}
